import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@/services/auth.service';
import { handleApiError } from '@/utils/errorHandler';

export interface UseOTPVerificationResult {
  // Form state
  otp: string;
  setOtp: (value: string) => void;
  
  // Loading state
  loading: boolean;
  
  // Error state
  error: string | null;
  
  // Countdown state
  countdown: number;
  
  // Email from URL params
  email: string;
  
  // Actions
  handleSubmit: () => Promise<void>;
  handleResendOTP: () => Promise<void>;
  handleGoBack: () => void;
  handleGoToLogin: () => void;
  formatTime: (seconds: number) => string;
}

/**
 * Hook xử lý logic xác thực OTP
 * - Lấy email từ URL params
 * - Verify OTP
 * - Resend OTP với countdown 60 giây
 * - Điều hướng sau khi verify thành công
 */
export const useOTPVerification = (): UseOTPVerificationResult => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  /**
   * Countdown timer cho resend OTP
   * - Tự động giảm mỗi giây
   * - Dừng khi countdown = 0
   */
  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [countdown]);

  /**
   * Format thời gian countdown thành chuỗi "XXs"
   */
  const formatTime = useCallback((seconds: number): string => {
    return `${seconds.toString().padStart(2, '0')}s`;
  }, []);

  /**
   * Xử lý submit OTP
   * - Validate OTP đủ 6 chữ số
   * - Validate email có trong URL params
   * - Gọi API verify OTP
   * - Điều hướng đến trang reset password nếu thành công
   */
  const handleSubmit = useCallback(async () => {
    if (otp.length !== 6) {
      message.error('Vui lòng nhập đầy đủ 6 chữ số OTP');
      return;
    }

    if (!email) {
      message.error('Không tìm thấy email. Vui lòng quay lại trang quên mật khẩu.');
      navigate('/forgot-password');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await authService.verifyOTP(email, otp);
      message.success('Xác thực OTP thành công!');
      navigate(`/reset-password?email=${encodeURIComponent(email)}&otp=${otp}`);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Mã OTP không chính xác. Vui lòng thử lại.';
      handleApiError(err, errorMessage);
      setError(errorMessage);
      setOtp(''); // Clear OTP để user nhập lại
    } finally {
      setLoading(false);
    }
  }, [otp, email, navigate]);

  /**
   * Xử lý gửi lại OTP
   * - Validate email có trong URL params
   * - Kiểm tra countdown (chỉ cho phép gửi lại sau 60 giây)
   * - Gọi API forgotPassword để gửi lại OTP
   * - Reset countdown về 60 giây
   */
  const handleResendOTP = useCallback(async () => {
    if (!email) {
      message.error('Không tìm thấy email');
      return;
    }

    if (countdown > 0) {
      return; // Đang trong thời gian chờ, không cho gửi lại
    }

    setLoading(true);
    setError(null);
    
    try {
      await authService.forgotPassword(email);
      message.success('Mã OTP mới đã được gửi đến email của bạn');
      setOtp(''); // Clear OTP hiện tại
      setCountdown(60); // Bắt đầu countdown 60 giây
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Không thể gửi lại mã OTP. Vui lòng thử lại.';
      handleApiError(err, errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [email, countdown]);

  /**
   * Điều hướng quay lại trang quên mật khẩu
   */
  const handleGoBack = useCallback(() => {
    navigate('/forgot-password');
  }, [navigate]);

  /**
   * Điều hướng về trang đăng nhập
   */
  const handleGoToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return {
    otp,
    setOtp,
    loading,
    error,
    countdown,
    email,
    handleSubmit,
    handleResendOTP,
    handleGoBack,
    handleGoToLogin,
    formatTime,
  };
};

